import { memo, FC, useState, startTransition, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Colors, ModalProps, Statistic } from 'Types';
import * as yup from 'yup';
import {
	Button,
	ChooseIconStatisticCard,
	CustomIconStatisticCard,
	FontAwesome,
	ModalBox,
	ModalBoxFooter,
	SubmitButton
} from 'Elements';
import { Col, FormGroup, Label, ModalBody } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';

interface Props extends ModalProps {
	title: string;
	selectedStatistic: any;
	statisticItems: Statistic[];
	onChangeValue: (
		objKey: string,
		key: string,
		value: string | boolean
	) => void;
}

export const ModalBoxAddStatistic: FC<Props> = memo(
	({
		isOpen,
		onClose,
		title,
		statisticItems,
		selectedStatistic,
		onChangeValue
	}) => {
		const { t } = useTranslation();
		const [activeTab, setActiveTab] = useState(0);

		const schema = yup.object().shape({
			title: yup
				.string()
				.max(50)
				.required(t('validation.title.required')),
			text: yup
				.string()
				.max(70)
				.required(t('validation.short-description.required')),
			icon: yup.string().required(t('validation.icon.required'))
		});

		const {
			handleSubmit,
			register,
			setValue,
			watch,
			reset,
			formState: { errors, isSubmitting, isDirty }
		} = useForm<any>({
			resolver: yupResolver(schema)
		});

		const valueConfig = {
			shouldDirty: true,
			shouldValidate: true
		};

		const handleClose = () => {
			startTransition(() => {
				onClose && onClose();
			});
		};

		const submitHandler: SubmitHandler<any> = (data) => {
			const api_data = {
				...data,
				isActive: true
			};
			const newList: any = [api_data];
			if (selectedStatistic) {
				statisticItems?.forEach((item) => {
					if (
						selectedStatistic?.title + selectedStatistic?.text !==
						item.title + item?.text
					) {
						newList.push(item);
					}
				});
			} else {
				statisticItems?.forEach((item) => {
					newList.push(item);
				});
			}
			onChangeValue('statisticItems', '', newList as any);
			onClose && onClose();
		};

		useEffect(() => {
			if (selectedStatistic) {
				reset(selectedStatistic);
			} else {
				reset({});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isOpen]);

		return (
			<ModalBox
				isOpen={isOpen}
				size="lg"
				onClose={handleClose}
				title={title}>
				{/* {isLoading && <LoadingContent />} */}
				<form onSubmit={handleSubmit(submitHandler)}>
					{/* {!isLoading && ( */}
					<>
						<ModalBody
							style={{
								height: '25rem',
								overflowY: 'auto'
							}}>
							<form onSubmit={handleSubmit(submitHandler)}>
								<FormGroup>
									<Label htmlFor="title">
										{t('forms.title')}
										<small className="ms-1">
											({t('validation.required')})
										</small>
									</Label>
									<input
										{...register('title')}
										type="text"
										id="title"
										className="inputbox w-100"
									/>
									{errors.title && (
										<div className="invalid-feedback d-block">
											{errors.title.message?.toString()}
										</div>
									)}
								</FormGroup>
								<FormGroup>
									<Label htmlFor="text">
										{t('forms.short-desc')}
										<small className="ms-1">
											({t('validation.required')})
										</small>
									</Label>
									<input
										{...register('text')}
										type="text"
										id="text"
										className="inputbox w-100"
									/>
									{errors.text && (
										<div className="invalid-feedback d-block">
											{errors.text.message?.toString()}
										</div>
									)}
								</FormGroup>
								<FormGroup>
									<div>
										<Label htmlFor="icon">
											{t('forms.icon')}
											<small className="ms-1">
												({t('validation.required')})
											</small>
										</Label>
										<div
											style={{
												borderRadius: '8px'
											}}
											className="p-4 bg-primary_o-2">
											<div className="d-flex align-items-center gap-3">
												<div
													className="bg-white d-flex justify-content-center align-items-center border border-primary p-2"
													style={{
														borderRadius: '8px',
														width: '54px',
														height: '54px'
													}}>
													{watch('icon') && (
														<FontAwesome
															icon={
																watch(
																	`icon`
																) as any
															}
															size="2x"
															className="text-primary"
														/>
													)}
												</div>
												<div className="d-flex flex-column">
													{watch('icon') ? (
														<>
															<span className="fw-400 fs-base">
																{watch('icon')}
															</span>
															<span>
																{t(
																	'text.currently-selected-icon'
																)}
															</span>
														</>
													) : (
														<div className="flex-1">
															{t(
																'forms.icon-preview'
															)}
														</div>
													)}
												</div>
											</div>
										</div>
										{errors.icon && (
											<div className="invalid-feedback d-block">
												{errors.icon.message?.toString()}
											</div>
										)}
									</div>
								</FormGroup>
								<FormGroup>
									<div
										className="border"
										style={{
											borderRadius: '8px 8px 0 0'
										}}>
										<div className="d-flex">
											<Col
												style={{
													borderRight:
														'1px solid #ccc',
													padding: '8px',
													cursor: 'pointer'
												}}
												onClick={() => setActiveTab(0)}
												className={`text-center ${
													activeTab === 0
														? 'bg-primary_o-2'
														: 'bg-white'
												}`}>
												<div className="fs-sm fw-500">
													{t('tab.choose-icon')}
												</div>
											</Col>
											<Col
												style={{
													borderRight:
														'1px solid #ccc',
													padding: '8px',
													cursor: 'pointer'
												}}
												onClick={() => setActiveTab(1)}
												className={`text-center ${
													activeTab === 1
														? 'bg-primary_o-2'
														: 'bg-white'
												}`}>
												<div className="fs-sm fw-500">
													{t('tab.custom-icon')}
												</div>
											</Col>
										</div>
										<div
											style={{
												borderTop: '1px solid #ccc'
											}}>
											{activeTab === 0 && (
												<ChooseIconStatisticCard
													selected={watch('icon')}
													onClick={(icon) => {
														setValue(
															'icon',
															icon,
															valueConfig
														);
													}}
												/>
											)}
											{activeTab === 1 && (
												<CustomIconStatisticCard
													selected={watch('icon')}
													onChange={(icon) => {
														setValue(
															'icon',
															icon,
															valueConfig
														);
													}}
												/>
											)}
										</div>
									</div>
								</FormGroup>
							</form>
						</ModalBody>
						<ModalBoxFooter className="justify-content-end mt-3 d-fixed">
							<Button
								color={Colors['white-primary']}
								onClick={handleClose}>
								{t('button.cancel')}
							</Button>
							<SubmitButton
								className="ms-4"
								isDisable={!isDirty}
								isSubmitting={isSubmitting}
							/>
						</ModalBoxFooter>
					</>
					{/* )} */}
				</form>
			</ModalBox>
		);
	}
);

ModalBoxAddStatistic.displayName = 'ModalBoxAddStatistic';
